import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import CodeSnippet from "@/components/CodeSnippet";
import HowItWorks from "@/components/landing/HowItWorks";

const LandingPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState("OpenAI Py");

    return (
        <div className="bg-background text-foreground min-h-screen flex flex-col">
            <Navbar />

            <header className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
                {/* Gradient Background */}
                <div className="absolute inset-0 -z-10 bg-gradient-to-b from-emerald-50/50 to-background dark:from-emerald-950/20 dark:to-background pointer-events-none" />

                <div className="container mx-auto px-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                        <div>
                            <Badge variant="secondary" className="px-4 py-1.5 text-sm font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300">
                                SigLLM | LLM Cost Optimization Engine
                            </Badge>
                            <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl xl:text-7xl">
                                Cut LLM spend <br /><span className="text-primary">without changing your app stack</span>
                            </h1>
                            <p className="mt-6 text-xl leading-relaxed text-muted-foreground">
                                Routing, semantic caching, and spend controls for AI teams.
                            </p>
                            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                                <Button asChild size="lg" className="h-12 rounded-xl px-8 text-base font-semibold shadow-emerald-500/20 shadow-lg transition-all hover:scale-105 hover:shadow-emerald-500/40">
                                    <Link to="/register">Start Free</Link>
                                </Button>
                                <Button asChild variant="ghost" size="lg" className="h-12 rounded-xl px-4 text-base font-semibold text-muted-foreground hover:text-foreground">
                                    <a href="mailto:sales@sigllm.com?subject=LLM Cost Audit Inquiry">Book Cost Audit</a>
                                </Button>
                            </div>
                        </div>
                        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                            <div className="absolute -top-12 -right-12 h-72 w-72 rounded-full bg-emerald-400/30 blur-3xl opacity-50 animate-pulse"></div>
                            <Card className="relative rounded-3xl shadow-2xl border-0 overflow-hidden ring-1 ring-border/50 animate-float">
                                <CardHeader className="bg-muted/50 pb-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex space-x-2">
                                            <div className="h-3 w-3 rounded-full bg-red-400/80" />
                                            <div className="h-3 w-3 rounded-full bg-amber-400/80" />
                                            <div className="h-3 w-3 rounded-full bg-emerald-400/80" />
                                        </div>
                                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                                            Routing Logic
                                        </CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4 p-6 bg-card/50 backdrop-blur-sm">
                                    <div className="rounded-xl border bg-background p-4 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-3">
                                            <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                                            <strong className="text-foreground">SigLLM Core Engine</strong>
                                        </div>
                                        <p className="mt-1 text-sm text-muted-foreground ml-5">One-line integration with state-of-the-art LLM cost optimization—all in one box.</p>
                                    </div>
                                    <div className="rounded-xl border bg-background p-4 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-3">
                                            <div className="h-2 w-2 rounded-full bg-primary" />
                                            <strong className="text-foreground">Semantic Caching</strong>
                                        </div>
                                        <p className="mt-1 text-sm text-muted-foreground ml-5">Vector-based caching that eliminates repetitive queries through LLM-powered context search.</p>
                                    </div>
                                    <div className="rounded-xl border bg-background p-4 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center gap-3">
                                            <div className="h-2 w-2 rounded-full bg-primary" />
                                            <strong className="text-foreground">LLMs AI Routing</strong>
                                        </div>
                                        <p className="mt-1 text-sm text-muted-foreground ml-5">An AI agent analyzes query complexity and intelligently routes requests to the optimal LLM </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </header>

            <section className="py-24 bg-gradient-to-b from-muted/40 via-background to-background">
                <div className="container mx-auto px-4">
                    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                        <div>
                            <Badge variant="secondary" className="px-4 py-1.5 text-sm font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300">
                                Integrate in minutes
                            </Badge>
                            <h2 className="mt-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                                Drop in SigLLM with three lines of code
                            </h2>
                            <p className="mt-4 text-lg text-muted-foreground">
                                Point your SDK at SigLLM and keep the rest of your stack unchanged. Start routing
                                across providers instantly while getting unified observability.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background px-4 py-2 shadow-sm">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    Zero vendor lock-in
                                </div>
                                <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background px-4 py-2 shadow-sm">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    Intelligent routing
                                </div>
                                <div className="flex items-center gap-2 rounded-full border border-border/60 bg-background px-4 py-2 shadow-sm">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    Semantic caching
                                </div>
                            </div>
                        </div>
                        <Card className="relative overflow-hidden border border-border/60 bg-white text-foreground shadow-2xl">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/60 via-white to-purple-100/50" />
                            <CardHeader className="relative space-y-6 border-b border-border/60 bg-white/70 pb-6">
                                <div className="flex items-center gap-2">
                                    <div className="h-3 w-3 rounded-full bg-red-400/80" />
                                    <div className="h-3 w-3 rounded-full bg-amber-400/80" />
                                    <div className="h-3 w-3 rounded-full bg-emerald-400/80" />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {["Node.js", "Python", "OpenAI JS", "OpenAI Py", "cURL"].map((label) => (
                                        <button
                                            key={label}
                                            onClick={() => setActiveTab(label)}
                                            className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide shadow-sm transition-colors ${activeTab === label
                                                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                                : "bg-white text-muted-foreground border-border/60 hover:bg-gray-50"
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </CardHeader>
                            <CardContent className="relative p-6">
                                <pre className="rounded-2xl border border-border/60 bg-slate-950 p-5 text-sm leading-relaxed text-slate-100 shadow-inner overflow-x-auto">
                                    <CodeSnippet language={activeTab} />
                                </pre>
                                <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                                    Replace your base URL and start routing instantly.
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            <HowItWorks />

            <section id="pricing" className="py-24">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            Simple, Transparent Pricing
                        </h2>
                        <p className="mt-4 text-lg text-muted-foreground">
                            Usage-Based Freemium Model. We only make money when you save money.
                        </p>
                    </div>
                    <div className="grid gap-8 lg:grid-cols-4 max-w-7xl mx-auto">
                        {/* Sandbox Plan */}
                        <Card className="flex flex-col shadow-sm hover:shadow-md transition-all">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold uppercase tracking-wide text-muted-foreground">Sandbox</CardTitle>
                                <div className="mt-4 text-4xl font-bold text-foreground">$0 <span className="text-lg font-normal text-muted-foreground">/mo</span></div>
                            </CardHeader>
                            <CardContent className="flex-1">
                                <ul className="space-y-4 text-muted-foreground text-sm">
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Up to 1M tokens</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Access to core routing engine</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> 10 Days Cache Retention</li>
                                </ul>
                            </CardContent>
                            <div className="p-6 pt-0 mt-auto">
                                <Button asChild variant="outline" className="w-full">
                                    <Link to="/register">Start for Free</Link>
                                </Button>
                            </div>
                        </Card>

                        {/* Starter Plan */}
                        <Card className="flex flex-col relative border-primary shadow-md hover:shadow-lg transition-all bg-background">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                                    Starter
                                </CardTitle>
                                <div className="mt-4 text-4xl font-bold text-foreground">
                                    $20 <span className="text-lg font-normal text-muted-foreground">/mo</span>
                                </div>
                            </CardHeader>
                            <CardContent className="flex-1">
                                <ul className="space-y-4 text-muted-foreground text-sm">
                                    <li className="flex items-center gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                                        Up to 5M tokens
                                    </li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                                        Access to core routing engine
                                    </li>
                                    <li className="flex items-center gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                                        Vector-based semantic caching
                                    </li>
                                    <li className="flex items-center gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                                        Usage & cost analytics
                                    </li>
                                </ul>
                            </CardContent>
                            <div className="p-6 pt-0 mt-auto">
                                <Button asChild className="w-full text-lg h-12 shadow-emerald-500/25 shadow-lg" size="lg">
                                    <Link to="/register">Get started</Link>
                                </Button>
                            </div>
                        </Card>

                        {/* Pro Plan */}
                        <Card className="flex flex-col relative border-primary shadow-2xl scale-105 z-10 bg-background">
                            <div className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-1/2">
                                <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 px-3 py-1 text-xs uppercase tracking-wider">Most Popular</Badge>
                            </div>
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold uppercase tracking-wide text-primary">Pro</CardTitle>
                                <div className="mt-4 text-4xl font-bold text-foreground">$79 <span className="text-lg font-normal text-muted-foreground">/mo</span></div>
                            </CardHeader>
                            <CardContent className="flex-1">
                                <ul className="space-y-4 text-foreground text-sm font-medium">
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> <span className="leading-snug">Up to 50 Million tokens</span></li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Unlimited tokens with overuse pricing</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Smart LLM Cascade Routing</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Semantic Caching</li>
                                </ul>
                            </CardContent>
                            <div className="p-6 pt-0 mt-auto">
                                <Button asChild className="w-full text-lg h-12 shadow-emerald-500/25 shadow-lg" size="lg">
                                    <Link to="/register">Get Started</Link>
                                </Button>
                            </div>
                        </Card>

                        {/* Enterprise Plan */}
                        <Card className="flex flex-col shadow-sm hover:shadow-md transition-all">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold uppercase tracking-wide text-muted-foreground">Enterprise</CardTitle>
                                <div className="mt-4 text-4xl font-bold text-foreground">Custom</div>
                            </CardHeader>
                            <CardContent className="flex-1">
                                <ul className="space-y-4 text-muted-foreground text-sm">
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Unlimited requests</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> SSO & Audit Logs</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Custom Routing Logic</li>
                                    <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" /> Private Cloud Deployment</li>
                                </ul>
                            </CardContent>
                            <div className="p-6 pt-0 mt-auto">
                                <Button variant="outline" className="w-full" asChild>
                                    <a href="mailto:sales@sigllm.com">Contact Sales</a>
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            <footer className="border-t bg-muted/50 py-16">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-2xl font-bold text-foreground">Ready to Optimize Your AI Stack?</h2>
                    <p className="mt-4 text-muted-foreground mb-8">
                        Join hundreds of engineering teams saving money with SigLLM.
                    </p>
                    <div className="flex flex-col justify-center gap-4 sm:flex-row">
                        <Button asChild size="lg" className="rounded-xl px-8">
                            <Link to="/register">Start Free</Link>
                        </Button>
                        <Button size="lg" variant="outline" className="rounded-xl px-8" asChild>
                            <a href="mailto:support@sigllm.com">Contact Support</a>
                        </Button>
                    </div>
                    <div className="mt-12 flex items-center justify-center gap-2 text-sm text-muted-foreground opacity-60">
                        <span>© {new Date().getFullYear()} SigLLM. All rights reserved.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;